import { Box, Card, CardActionArea, Typography, Tooltip } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { attentionSeverityColors } from "../palette";
import { AttentionItem, AttentionSeverity } from "../types";
import { attentionItemCatalog } from "../../services/attentionCatalog";

const severityOrder: Record<AttentionSeverity, number> = { critical: 0, warning: 1, info: 2 };

const severityIcon: Record<AttentionSeverity, React.ReactNode> = {
  critical: <ErrorOutlineIcon sx={{ fontSize: 15 }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 15 }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: 15 }} />,
};

const severityLabel: Record<AttentionSeverity, string> = {
  critical: "Critical",
  warning: "High",
  info: "Normal",
};

// Severity always shows as icon + word + colour together, never colour alone.
const SeverityPill = ({ severity, children }: { severity: AttentionSeverity; children: React.ReactNode }) => {
  const accent = attentionSeverityColors[severity];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        color: accent,
        backgroundColor: `${accent}14`,
        border: `1px solid ${accent}2e`,
      }}
    >
      {children}
    </Box>
  );
};

type AttentionPanelProps = {
  items: AttentionItem[];
};

export const AttentionPanel = ({ items }: AttentionPanelProps) => {
  const navigate = useNavigate();

  // wording and links come from the catalog at render time, not from the cached response, so a
  // dashboard cached before a link changed still opens the right page
  const sortedItems = items
    .map((item): AttentionItem => ({ ...item, ...(attentionItemCatalog[item.id] ?? {}) }))
    .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  const counts = items.reduce(
    (acc, item) => ({ ...acc, [item.severity]: (acc[item.severity] ?? 0) + 1 }),
    {} as Partial<Record<AttentionSeverity, number>>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
          Action Center
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {items.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {(["critical", "warning", "info"] as AttentionSeverity[])
                .filter((severity) => counts[severity])
                .map((severity) => (
                  <SeverityPill key={severity} severity={severity}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: attentionSeverityColors[severity] }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                      {counts[severity]} {severityLabel[severity]}
                    </Typography>
                  </SeverityPill>
                ))}
            </Box>
          )}

          <Tooltip title="Full action center list — coming soon">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "default",
                color: "primary.main",
                "&:hover .view-all-arrow": { transform: "translateX(2px)" },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                View All
              </Typography>
              <ArrowForwardIcon
                className="view-all-arrow"
                sx={{ fontSize: 15, transition: "transform 0.2s ease" }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {items.length === 0 ? (
        <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(22, 163, 74, 0.1)",
                color: "success.main",
                flexShrink: 0,
              }}
            >
              <TaskAltIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#0f172a" }}>
                You're all caught up
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Nothing needs action right now — check back later today.
              </Typography>
            </Box>
          </Box>
        </Card>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(auto-fill, minmax(280px, 1fr))",
            },
            gap: 2,
          }}
        >
          {sortedItems.map((item) => {
            const accent = attentionSeverityColors[item.severity];
            return (
              <Card
                key={item.id}
                elevation={0}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
                    borderColor: `${accent}55`,
                  },
                  "&:hover .attention-arrow": {
                    transform: "translateX(3px)",
                  },
                }}
              >
                {/* left accent bar */}
                <Box sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 4, backgroundColor: accent }} />

                <CardActionArea
                  onClick={() => navigate(item.route)}
                  sx={{ height: "100%", p: 2.25, pl: 2.75, display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start" }}
                >
                  <Box>
                    <SeverityPill severity={item.severity}>
                      {severityIcon[item.severity]}
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {severityLabel[item.severity]}
                      </Typography>
                    </SeverityPill>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 1.75 }}>
                    <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
                      {item.metric}
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#0f172a", lineHeight: 1.3 }}>
                      {item.label}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.75 }}>
                    {item.detail}
                  </Typography>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "primary.main",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {item.actionLabel}
                    </Typography>
                    <ArrowForwardIcon
                      className="attention-arrow"
                      sx={{ fontSize: 15, transition: "transform 0.2s ease" }}
                    />
                  </Box>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
